import { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { BarChart3, Palette, FileVideo, AlertCircle, ArrowLeft, Play, Square, RotateCcw, Copy, Eye, EyeOff, Sliders, ChevronDown, ChevronUp, History, Grid3X3, Settings, Save, CheckCircle, MessageSquare, Info } from "lucide-react";
import { StatusBanner } from "@/components/common/StatusBanner";
import { TelemetryBadges } from "../components/TelemetryBadges";
import { TelemetryPanel } from "../components/TelemetryPanel";
import { ExportMenu } from "../components/ExportMenu";
import { useVideo } from "../data/useVideo";
import { useSignedPlaybackUrl } from "../data/useSignedPlaybackUrl";
import { getVideoByAssetId } from "@/lib/video-by-asset";
import { VideoPlayer } from "../components/VideoPlayer";
import { AnalysisError } from "../components/AnalysisError";
import { logIdPlumbing } from "../lib/idPlumbingLogger";
import { isWithinSwingLabLimits, getLimitWarningMessage } from "../utils/limits";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { formatDistanceToNow } from "date-fns";
import { AnalysisAPI } from '@/lib/api/analysis';
import { useMpOverlay } from "../analysis/useMpOverlay";
import OverlayCanvas from "../analysis/OverlayCanvas";
import { OverlayController } from "@/features/telemetry2/OverlayController";
import type { PoseData } from "@/features/telemetry2/types";
import { RecentRunsList } from "../components/RecentRunsList";
// import { MuxPlaybackDebug } from "../components/MuxPlaybackDebug";
import { supabase } from "@/integrations/supabase/client";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { SavedVideosList } from "@/components/SavedVideosList";
import { toast } from "sonner";
import { ReportIssueModal } from '@/features/feedback/ReportIssueModal';
import { useFeedbackModal } from '@/features/feedback/useFeedbackModal';
import type { SwingRunSummary, SwingLabVideo } from "../types";
import { gate } from "@/lib/tripwire";
import { createTelemetryReader, type TelemetryReader } from "@/lib/telemetry/readStream";
import { ProcessingNarrative } from "@/components/processing/ProcessingNarrative";

function formatDuration(seconds?: number | null): string {
  if (!seconds) return '—';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export function SwingLabAnalysisPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const videoId = searchParams.get('id');
  const assetId = searchParams.get('asset');
  const runIdParam = searchParams.get('runId'); // New flow uses runId
  const analysisIdParam = searchParams.get('analysisId');
  const [resolvedVideoId, setResolvedVideoId] = useState<string | undefined>(videoId || undefined);
  const { isOpen, openModal, closeModal, context } = useFeedbackModal();
  
  // CRITICAL: Redirect safety - if analysisId or runId is available, redirect to authoritative Analysis page
  // This route must NEVER render Analysis Spine, Explanations, Scores, or Visual Feedback
  useEffect(() => {
    const analysisId = analysisIdParam || runIdParam;
    if (analysisId) {
      // Immediately redirect to authoritative Analysis page
      navigate(`/analysis/${analysisId}`, { replace: true });
      return;
    }
    
    // Legacy format: ?id=VIDEO_ID - try to resolve to analysisId
    if (videoId && !analysisId && !runIdParam) {
      const resolveLegacyVideoId = async () => {
        try {
          const { getAnalysisIdForVideo } = await import('../data/runs');
          // Try to resolve videoId as video_asset_id
          const resolvedAnalysisId = await getAnalysisIdForVideo(videoId);
          if (resolvedAnalysisId) {
            navigate(`/analysis/${resolvedAnalysisId}`, { replace: true });
            return;
          }
          // If videoId is not an asset_id, try to find latest run for this video
          // This handles legacy video table IDs
          const { data: runs } = await supabase
            .from('swing_analysis_runs')
            .select('id')
            .eq('status', 'completed')
            .order('created_at', { ascending: false })
            .limit(1)
            .single();
          
          if (runs?.id) {
            navigate(`/analysis/${runs.id}`, { replace: true });
          }
        } catch (error) {
          console.error('[SwingLabAnalysisPage] Error resolving legacy videoId:', error);
          // Show minimal placeholder if resolution fails
        }
      };
      resolveLegacyVideoId();
    }
  }, [analysisIdParam, runIdParam, videoId, navigate]);
  
  // FIX: Read query parameters for debug and force show
  const forceShowAI = searchParams.get('forceShowAI') === '1';
  const forceDebug = searchParams.get('debug') === '1';
  
  // If we have an asset ID but no video ID, resolve it
  useEffect(() => {
    if (assetId && !videoId) {
      getVideoByAssetId(assetId)
        .then(({ data, error }) => {
          if (error) {
            console.error('Failed to resolve asset ID to video ID:', error);
            toast.error('Video not found', {
              description: `Could not find video with asset ID: ${assetId}`
            });
          } else if (data) {
            setResolvedVideoId(data.id);
            // Update URL to use video ID for consistency
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.delete('asset');
            newUrl.searchParams.set('id', data.id);
            window.history.replaceState({}, '', newUrl.toString());
          }
        });
    } else {
      setResolvedVideoId(videoId || undefined);
    }
  }, [videoId, assetId]);
  
  const { data: video, isLoading: videoLoading, error: videoError } = useVideo(resolvedVideoId);
  const [videoOverride, setVideoOverride] = useState<SwingLabVideo | null>(null);

  // FIX: Declare currentVideo BEFORE any useEffect that references it to avoid TDZ
  const currentVideo = videoOverride || video;


  // Cleanup telemetry reader when video changes
  useEffect(() => {
    if (telemetryReader) {
      console.log('[AnalysisPage] Video changed, disposing telemetry reader');
      telemetryReader.dispose();
      setTelemetryReader(null);
      setTelemetryReady(false);
    }
  }, [resolvedVideoId]); // Only trigger when video ID changes
  
  // Debug: Log video data loading state + auto-load telemetry
  useEffect(() => {
    if (import.meta.env.DEV) {
      console.log('[AnalysisPage] init', {
        video_id: resolvedVideoId,
        assetId: currentVideo?.muxAssetId,
        playbackId: currentVideo?.muxPlaybackId,
        hasVideo: !!currentVideo,
        videoLoading,
        videoError: videoError?.message,
        playbackPolicy: currentVideo?.playbackPolicy,
        status: currentVideo?.status
      });
    }

    // Auto-load telemetry on page load if video is ready
    if (currentVideo && currentVideo.muxAssetId && !videoLoading && !videoError) {
      // Define the telemetry loading function inline to avoid TDZ
      const autoLoadTelemetry = async () => {
        try {
          // Find the most recent completed run for this video
          const { data: runs, error: runsError } = await supabase
            .from('swing_analysis_runs')
            .select('id, storage_path, processed_frames')
            .eq('video_asset_id', currentVideo.muxAssetId)
            .eq('status', 'completed')
            .order('created_at', { ascending: false })
            .limit(1);

          if (runsError) throw runsError;
          if (!runs || runs.length === 0) {
            console.log('[AnalysisPage] No existing telemetry found for video');
            return;
          }

          const latestRun = runs[0];
          console.log('[AnalysisPage] Found existing telemetry, auto-loading...', {
            runId: latestRun.id,
            storagePath: latestRun.storage_path,
            processedFrames: latestRun.processed_frames
          });
          
          // Load the run data programmatically (no download)
          const runSummary = { 
            id: latestRun.id, 
            storagePath: latestRun.storage_path 
          } as SwingRunSummary;
          // We'll call handleReloadRun when it's available
          if (handleReloadRunRef.current) {
            await handleReloadRunRef.current(runSummary);
          }
        } catch (err) {
          console.warn('Failed to auto-load telemetry:', err);
          // Don't show error toast for auto-load failures
        }
      };

      // Wait for video element to be ready before loading telemetry
      const videoElement = videoRef?.current;
      if (videoElement && videoElement.readyState >= 2) {
        autoLoadTelemetry();
      } else if (videoElement) {
        // Wait for video to be ready
        const handleCanPlay = () => {
          autoLoadTelemetry();
          videoElement.removeEventListener('canplay', handleCanPlay);
        };
        videoElement.addEventListener('canplay', handleCanPlay);
      } else {
        // Fallback: load after short delay if no video element yet
        setTimeout(() => autoLoadTelemetry(), 1000);
      }
    }
  }, [resolvedVideoId, currentVideo, videoLoading, videoError]);

  // FIX: Add video status polling for processing videos without page refresh
  useEffect(() => {
    if (!currentVideo || currentVideo.status === 'ready' || videoLoading || videoError) return;
    
    // Poll video status if it's still processing
    if (currentVideo.status === 'processing' || currentVideo.status === 'uploading') {
      console.log('[VIDEO-POLL] Starting status polling for', { videoId: resolvedVideoId, status: currentVideo.status });
      
      const pollInterval = setInterval(async () => {
        try {
          // Import getVideoById and transform dynamically
          const { getVideoById } = await import('@/lib/videos');
          const { transformVideoRecord } = await import('../data/transform');
          const { data: updatedVideo, error } = await getVideoById(resolvedVideoId!);
          
          if (error) {
            console.error('[VIDEO-POLL] Error polling video status:', error);
            clearInterval(pollInterval);
            return;
          }
          
          console.log('[VIDEO-POLL] Status check:', { 
            videoId: resolvedVideoId, 
            oldStatus: currentVideo.status, 
            newStatus: updatedVideo?.status 
          });
          
          if (updatedVideo && updatedVideo.status === 'ready') {
            console.log('[VIDEO-POLL] Video ready, updating state');
            // FIX: Update video state without page refresh
            const transformedVideo = transformVideoRecord(updatedVideo);
            setVideoOverride(transformedVideo);
            clearInterval(pollInterval);
            
            toast.success('Video processing complete!', {
              description: 'Video is now ready for analysis'
            });
          }
        } catch (err) {
          console.error('[VIDEO-POLL] Polling error:', err);
          clearInterval(pollInterval);
        }
      }, 2000); // Poll every 2 seconds
      
      return () => {
        console.log('[VIDEO-POLL] Cleanup polling for', resolvedVideoId);
        clearInterval(pollInterval);
      };
    }
  }, [currentVideo?.status, videoLoading, videoError, resolvedVideoId]);
  
  const { 
    url: signedUrl, 
    isLoading: urlLoading, 
    error: urlError,
    refetch: refetchUrl,
    lastCall: lastSignerCall
  } = useSignedPlaybackUrl(
    // Only call signer for non-public videos
    currentVideo?.playbackPolicy === 'public' ? undefined : currentVideo?.muxPlaybackId || undefined
  );

  // MediaPipe overlay state
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoReady, setVideoReady] = useState(false);
  
  // TRIPWIRE: Video ref binding and ready signal establishment
  const handleVideoRef = useCallback((el: HTMLVideoElement | null) => {
    videoRef.current = el;
  }, []);
  
  // Establish reliable videoReady signal from real video events
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    
    const onReady = () => {
      setVideoReady(true);
    };
    
    // Check if already ready
    if (v.readyState >= 1) {
      onReady();
    } else {
      v.addEventListener('loadedmetadata', onReady);
      v.addEventListener('canplay', onReady);
    }
    
    return () => {
      v.removeEventListener('loadedmetadata', onReady);
      v.removeEventListener('canplay', onReady);
    };
  }, [videoRef.current]);
  
  const playerHostRef = useRef<HTMLDivElement>(null);
  const handleReloadRunRef = useRef<((run: SwingRunSummary) => Promise<void>) | null>(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [showDebugPanel, setShowDebugPanel] = useState(forceDebug);
  const [aiAnalysisLoading, setAiAnalysisLoading] = useState(false);
  const [aiAnalysisError, setAiAnalysisError] = useState<string | null>(null);
  const [telemetryFrames, setTelemetryFrames] = useState<any[]>([]);
  const [showAiJsonPanel, setShowAiJsonPanel] = useState(false);
  const [telemetryReader, setTelemetryReader] = useState<TelemetryReader | null>(null);
  const [telemetryReady, setTelemetryReady] = useState(false);
  const [currentPoseData, setCurrentPoseData] = useState<PoseData | null>(null); // S-3057A
  const [currentRunId, setCurrentRunId] = useState<string | null>(null);
  const [openaiDebugData, setOpenaiDebugData] = useState<any>(null);
  
  // Check for debug mode
  const isDebugMode = searchParams.get('debug') === '1';



  const [debugCounters, setDebugCounters] = useState({
    framesFound: 0,
    framesParsed: 0,
    framesSentToAI: 0,
    framesAckedByAI: 0,
    framesUsedByAI: 0
  });

  // Canonical source & invariants for MediaPipe IDs (NEVER use fallbacks)
  const playbackId = currentVideo?.muxPlaybackId;
  const videoAssetId = currentVideo?.muxAssetId;
  const idsReady = Boolean(playbackId && videoAssetId);

  // DEBUG: Log ID values with types and lengths
  console.log('[DEBUG][IDS] Raw video object:', { 
    hasVideo: !!currentVideo,
    muxPlaybackId: playbackId,
    muxAssetId: videoAssetId,
    playbackIdType: typeof playbackId,
    assetIdType: typeof videoAssetId,
    playbackIdLength: playbackId?.length,
    assetIdLength: videoAssetId?.length,
    idsReady,
    status: currentVideo?.status
  });

  logIdPlumbing('AnalysisPage wiring', { playbackId, assetId: videoAssetId });

  // Only log error if video is NOT in processing state
  if (!idsReady && currentVideo && currentVideo.status !== 'uploading' && currentVideo.status !== 'processing') {
    console.error('signer_no_ids: missing playbackId and/or assetId', { playbackId, assetId: videoAssetId, video: currentVideo });
  }

  // Show toast if IDs are missing AND video is not processing
  useEffect(() => {
    if (!idsReady && currentVideo && currentVideo.status !== 'uploading' && currentVideo.status !== 'processing') {
      toast.error('Missing IDs (playbackId/assetId). Cannot run analysis.');
    }
  }, [idsReady, currentVideo]);

  // Use the new MediaPipe overlay hook
  const {
    state: mpState,
    stats: mpStats,
    error: mpError,
    showOverlay,
    opacity,
    handsOnly,
    showSkeleton,
    savedLink,
    startAnalysis,
    stopAnalysis,
    persistCompletedRun,
    getBufferFrameCount,
    framesBuffered,
    hasUnsavedFrames,
    isPersisting,
    forceFlushBuffer,
    lastRunMeta,
    toggleOverlay,
    setOpacity,
    setHandsOnly,
    setShowSkeleton,
    getLatestFrame,
    canvasRef,
    idsReady: mpIdsReady,
    loadFramesIntoBuffer,
    setShowOverlay,
    getFrameAtTime,
    isLiveCapture
  } = useMpOverlay({
    playbackId,
    assetId: videoAssetId
  });

  // Button enablement logic per prompt requirements
  const stopEnabled =
    mpState === 'running' ||
    framesBuffered > 0 ||
    hasUnsavedFrames;

  const saveEnabled =
    !isPersisting &&
    (framesBuffered > 0 || hasUnsavedFrames);

  console.log('[UI][STOP]', { enabled: stopEnabled, mpState, framesBuffered });
  console.log('[UI][SAVE]', { enabled: saveEnabled, mpState, framesBuffered, hasUnsavedFrames, isPersisting });

  // SINGLE CANONICAL STOP AND PERSIST FUNCTION
  const handleStopAndPersist = useCallback(async (reason: 'ended' | 'pause_at_end' | 'manual_click') => {
    console.log('[MP][STOP]', { reason });
    
    const wd = setTimeout(() => {
      console.log('[WATCHDOG][PERSIST][ERROR]', {
        reason,
        hint: 'persistCompletedRun not invoked/awaited or returned early',
        have: {
          video_id: video?.id,
          muxAssetId: video?.muxAssetId,
          playbackId: video?.muxPlaybackId
        }
      });
    }, 3000);

    try {
      // First stop the analysis (cancels frame processing)
      await stopAnalysis();
      
      // Then explicitly persist the completed run
      await persistCompletedRun(reason);
    } finally {
      clearTimeout(wd);
      // Only here can we safely change any state that might unmount components
    }
  }, [stopAnalysis, persistCompletedRun, video]);

  // Auto-stop hooks from real video element - only when MediaPipe is actually running
  useEffect(() => {
    const v = videoRef.current;
    if (!v || mpState !== 'running') return; // FIX: Only add handlers when actually running

    const onEnded = () => {
      console.log('[UI] video:ended - stopping and persisting analysis');
      if (mpState === 'running') {
        handleStopAndPersist('ended'); // Stop and persist
      }
    };
    
    const onPauseMaybeEnd = () => {
      const epsilon = 0.05; // 50ms
      const currentTime = v.currentTime;
      const duration = v.duration;
      
      // Add debugging and more robust check
      console.log('[UI] video:pause event', { 
        currentTime: currentTime.toFixed(3), 
        duration: duration?.toFixed(3), 
        nearEnd: duration ? currentTime >= duration - epsilon : false,
        mpState 
      });
      
      // Only stop if we're actually near the end AND MediaPipe is running
      if (duration && currentTime >= duration - epsilon && mpState === 'running') {
        console.log('[UI] video:pause_at_end - stopping and persisting analysis');
        handleStopAndPersist('pause_at_end'); // Stop and persist
      }
    };

    v.addEventListener('ended', onEnded);
    v.addEventListener('pause', onPauseMaybeEnd);
    return () => {
      v.removeEventListener('ended', onEnded);
      v.removeEventListener('pause', onPauseMaybeEnd);
    };
  }, [mpState, handleStopAndPersist]);

  // Watchdog to auto-stop if MediaPipe doesn't capture frames (not video progress)
  useEffect(() => {
    if (mpState !== 'running') return;
    let lastFrameCount = framesBuffered;
    let startTime = Date.now();
    const id = setInterval(() => {
      if (framesBuffered === lastFrameCount && framesBuffered === 0) {
        const runningTime = Date.now() - startTime;
        if (runningTime > 10000) { // 10 seconds with no frames
          console.log('[WATCHDOG] no frames captured after 10s; stopping');
          stopAnalysis();
        }
      } else {
        startTime = Date.now(); // Reset timer if frames are being captured
      }
      lastFrameCount = framesBuffered;
    }, 2000);
    return () => clearInterval(id);
  }, [mpState, stopAnalysis, framesBuffered]);

  // Handle analysis success
  useEffect(() => {
    if (mpState === 'saved' && savedLink) {
      toast.success('Analysis saved successfully!', {
        description: 'NDJSON data has been saved to storage.',
        action: {
          label: 'Download',
          onClick: () => window.open(savedLink, '_blank')
        }
      });
    }
  }, [mpState, savedLink]);

  // Handle analysis errors
  useEffect(() => {
    if (mpError) {
      toast.error('Analysis failed', {
        description: mpError
      });
    }
  }, [mpError]);

  // FIX: Handler to load saved telemetry from a run using streaming readStream
  const handleReloadRun = useCallback(async (run: SwingRunSummary) => {
    try {
      console.log('[AnalysisPage] Loading saved run with readStream:', run.id);
      setCurrentRunId(run.id);
      
      if (!run.storagePath) {
        toast.error('No telemetry data found for this run');
        return;
      }

      // Dispose existing reader
      if (telemetryReader) {
        telemetryReader.dispose();
        setTelemetryReader(null);
        setTelemetryReady(false);
      }


      // S-3057A: Load pose_data for v2 overlay
      try {
        const { data: poseDataRow, error: poseError } = await supabase
          .from('swing_runs')
          .select('pose_data')
          .eq('id', run.id)
          .maybeSingle();
          
        if (poseError) {
          console.warn('[S-3057A] Failed to load pose_data:', poseError);
          setCurrentPoseData(null);
        } else if (poseDataRow?.pose_data) {
          console.log('[S-3057A] Loaded pose_data for v2 overlay:', {
            runId: run.id,
            frames: poseDataRow.pose_data.landmarks?.length || 0,
          });
          setCurrentPoseData(poseDataRow.pose_data as PoseData);
        } else {
          console.log('[S-3057A] No pose_data available for run:', run.id);
          setCurrentPoseData(null);
        }
      } catch (poseDataError) {
        console.warn('[S-3057A] Error loading pose_data:', poseDataError);
        setCurrentPoseData(null);
      }

      // Get signed URL for the NDJSON file
      const { data: signedData, error: signedError } = await supabase.storage
        .from('swing-lab')
        .createSignedUrl(run.storagePath, 3600); // 1 hour expiry

      if (signedError || !signedData?.signedUrl) {
        console.error('Failed to get signed URL:', signedError);
        toast.error('Failed to access telemetry data');
        return;
      }

      // Create streaming reader
      const reader = await createTelemetryReader(
        signedData.signedUrl,
        (bufferInfo) => {
          console.log(`[ReadStream] Ready! ${bufferInfo.frameCount} frames buffered, duration: ${bufferInfo.duration.toFixed(2)}s`);
          setTelemetryReady(true);
          setShowOverlay(true);
          
          // Update debug counters
          setDebugCounters(prev => ({
            ...prev,
            framesFound: bufferInfo.frameCount,
            framesParsed: bufferInfo.frameCount
          }));
          
          toast.success(`Streaming ${bufferInfo.frameCount} frames from saved analysis`);
        },
        30 // Ready when >= 30 frames buffered
      );

      setTelemetryReader(reader);
      
      // Create a wrapper function that uses the reader's getFrameAt
      const wrappedGetFrameAtTime = (time: number) => {
        return reader.getFrameAt(time);
      };
      
      // Load frames into overlay buffer using existing interface
      if (loadFramesIntoBuffer) {
        // Start with empty buffer - frames will be loaded as they stream in
        setTelemetryFrames([]);
        
        // The overlay will use getFrameAtTime which now points to our reader
        console.log('[ReadStream] Started streaming telemetry for replay');
      }
      
    } catch (err) {
      console.error('Failed to load run:', err);
      toast.error('Failed to load analysis data');
      setTelemetryReady(false);
    }
  }, [loadFramesIntoBuffer, setShowOverlay, telemetryReader]);

  // Update ref for handleReloadRun to avoid TDZ in useEffect
  useEffect(() => {
    handleReloadRunRef.current = handleReloadRun;
  }, [handleReloadRun]);

  // Cleanup telemetry reader on unmount
  useEffect(() => {
    return () => {
      if (telemetryReader?.dispose) {
        telemetryReader.dispose();
      }
    };
  }, [telemetryReader]);

  // FIX: Handler to trigger AI analysis with enrichment
  const handleRunAiAnalysis = useCallback(async () => {
    if (!idsReady || aiAnalysisLoading) return;

    try {
      // FIX: Clear stale UI flags at start
      setAiAnalysisLoading(true);
      setAiAnalysisError(null);
      

      // Use muxAssetId for resolver per prompt requirements
      const muxAssetId = currentVideo?.muxAssetId;
      if (!muxAssetId) {
        throw new Error('muxAssetId not available for run resolution');
      }
      
      console.log('[RUN][RESOLVE][SEARCH]', {
        searching_for_video_asset_id: muxAssetId,
        query: 'SELECT * FROM swing_analysis_runs WHERE video_asset_id = ? AND status = completed'
      });

      const { data: runs, error: runsError } = await supabase
        .from('swing_analysis_runs')
        .select('*')
        .eq('video_asset_id', muxAssetId)
        .eq('status','completed')
        .order('created_at',{ascending:false})
        .limit(1);

      console.log('[RUN][RESOLVE]', {
        video_id: muxAssetId,
        runId: runs?.[0]?.id ?? null,
        status: runs?.[0]?.status ?? null,
        totalFound: runs?.length ?? 0,
        error: runsError?.message ?? null
      });

      if (!runs || runs.length === 0) {
        console.warn('[RUN][RESOLVE][MISS]', { video_id: muxAssetId });
        throw new Error('No completed MediaPipe runs found for this video.');
      }

      const latestRun = runs[0];

      // Check run data quality
      const runFrames = latestRun.processed_frames ?? 0;
      const hasMetrics = !!latestRun.metrics;
      console.log('[RUN][READY]', { runId: latestRun.id, frames: runFrames, hasMetrics });
      toast.info('Analyzing swing with AI...');

      // FIX: Call the Edge Function with proper authentication
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Authentication required for AI analysis');
      }

      // Build AI payload per prompt requirements
      const aiBody = {
        runId: latestRun.id,
        video_asset_id: latestRun.video_asset_id,
        storage_path: latestRun.storage_path,
        metrics_available: !!latestRun.metrics,
        model: 'no-doubles-golf-coach',
        analysis_tier: 'professional',
        focus_area: 'full_swing',
        skill_level: 'mid_handicap',
        video_context: { 
          club: 'driver', 
          view: 'DTL', 
          shot_type: 'full_swing' 
        },
      };

      console.log('[AI][REQUEST][OPENAI]', {
        edge_function: 'swing-lab-analyze-agent-enhanced',
        body: aiBody,
      });

      // Call Edge Function with timing
      const t0 = performance.now();
      const { data, error } = await supabase.functions.invoke('swing-lab-analyze-agent-enhanced', {
        body: aiBody,
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      // Enhanced error logging to capture actual response
      console.log('[AI][RESPONSE]', { 
        data, 
        error, 
        hasData: !!data, 
        hasError: !!error,
        errorName: error?.name,
        errorMessage: error?.message
      });

      if (error) {
        // Try to get more details from the error
        console.error('[AnalysisPage] Edge Function error details:', {
          name: error.name,
          message: error.message,
          stack: error.stack,
          context: error.context,
          details: error.details
        });
        
        // Extract actual response body from error context
        if (error.context && typeof error.context.text === 'function') {
          try {
            const responseText = await error.context.text();
            console.error('[AnalysisPage] Edge Function response body:', responseText);
            
            try {
              const responseJson = JSON.parse(responseText);
              console.error('[AnalysisPage] Edge Function error details:', responseJson);
            } catch (parseErr) {
              console.error('[AnalysisPage] Response not JSON:', responseText);
            }
          } catch (textErr) {
            console.error('[AnalysisPage] Could not read response text:', textErr);
          }
        }
        
        throw error;
      }

      // FIX: Handle structured error responses from Edge Function
      if (data && !data.ok) {
        console.error('[AnalysisPage] Edge Function returned error:', data);
        throw new Error(`${data.error} (${data.code}): ${data.details || data.stage}`);
      }

      // Log response per prompt requirements
      console.log('[AI][RESPONSE][OPENAI]', { 
        ok: !error, 
        status: error ? 'error' : 'success', 
        processing_time_ms: Math.round(performance.now()-t0), 
        json: data
      });

      // CAPTURE OPENAI DEBUG DATA FOR UI DISPLAY
      if (data && (data.openaiRequest || data.openaiResponse || data.openaiContent)) {
        setOpenaiDebugData({
          request: data.openaiRequest,
          response: data.openaiResponse,
          content: data.openaiContent,
          timestamp: new Date().toISOString()
        });
        console.log('[DEBUG] Captured OpenAI debug data for UI display');
      }

      // DEBUG: Show what the edge function actually returned
      if (data) {
        console.log('[DEBUG] Edge Function Response Structure:', {
          ok: data.ok,
          stage: data.stage,
          enriched: data.enriched,
          hasAnalysis: !!data.analysis,
          analysisKeys: data.analysis ? Object.keys(data.analysis) : [],
          runId: data.runId
        });
        
        // Show the actual analysis content if present
        if (data.analysis) {
          console.log('[DEBUG] Edge Function Analysis Content:', {
            analysisType: typeof data.analysis,
            analysisStructure: data.analysis,
            hasFlaws: !!data.analysis.flaws,
            flawsCount: data.analysis.flaws?.length || 0,
            flawsList: data.analysis.flaws,
            hasCoachNarrative: !!data.analysis.coach_narrative,
            coachNarrative: data.analysis.coach_narrative
          });
        }
      }

      // FIX: Single query with graceful enriched column handling
      let updatedRun, enrichedData = null;
      
      try {
        // Try to get all data including enriched column in one query
        const { data: runWithEnriched, error: enrichedError } = await supabase
          .from('swing_analysis_runs')
          .select('ai_mediapipe_v1, telemetry_ai, ai_mediapipe_v1_enriched')
          .eq('id', latestRun.id)
          .single();
        
        if (enrichedError) {
          // If enriched column doesn't exist, fall back to basic query
          if (enrichedError.message?.includes('does not exist') || enrichedError.code === '42703') {
            console.log('ai_mediapipe_v1_enriched column not available, using basic query');
            
            // Fallback to basic query without enriched column
            const { data: basicRun, error: basicError } = await supabase
              .from('swing_analysis_runs')
              .select('ai_mediapipe_v1, telemetry_ai')
              .eq('id', latestRun.id)
              .single();
            
            if (basicError) throw basicError;
            updatedRun = basicRun;
            enrichedData = null;
          } else {
            console.warn('Unexpected error fetching enriched data:', enrichedError);
            throw enrichedError; // Re-throw unexpected errors
          }
        } else {
          // Successfully got all data including enriched
          updatedRun = runWithEnriched;
          enrichedData = runWithEnriched?.ai_mediapipe_v1_enriched;
          console.log('Successfully fetched enriched data:', { hasEnriched: !!enrichedData });
        }
      } catch (fetchError) {
        console.error('Failed to fetch analysis data:', fetchError);
        throw fetchError;
      }

      const analysisData = enrichedData || updatedRun.ai_mediapipe_v1;
      
      // DEBUG: Show raw OpenAI output and enriched data structure
      console.log('[DEBUG] RAW OPENAI OUTPUT:', {
        rawAI: updatedRun.ai_mediapipe_v1,
        enrichedData: enrichedData,
        usingEnriched: !!enrichedData,
        analysisDataUsed: analysisData
      });

      // DEBUG: Show detailed structure of what OpenAI actually returned
      if (updatedRun.ai_mediapipe_v1) {
        console.log('[DEBUG] OpenAI Raw Analysis Structure:', {
          hasFlaws: !!updatedRun.ai_mediapipe_v1.flaws,
          flawsLength: updatedRun.ai_mediapipe_v1.flaws?.length || 0,
          flawsArray: updatedRun.ai_mediapipe_v1.flaws,
          hasCoachNarrative: !!updatedRun.ai_mediapipe_v1.coach_narrative,
          coachNarrative: updatedRun.ai_mediapipe_v1.coach_narrative,
          hasDrills: !!updatedRun.ai_mediapipe_v1.drills,
          drillsLength: updatedRun.ai_mediapipe_v1.drills?.length || 0,
          fullStructure: Object.keys(updatedRun.ai_mediapipe_v1)
        });
      }

      // DEBUG: Show enriched data structure if available
      if (enrichedData) {
        console.log('[DEBUG] Enriched Data Structure:', {
          taxonomyVersion: enrichedData.taxonomy_version,
          globalPriorities: enrichedData.global_priorities,
          phaseSummariesLength: enrichedData.phase_summaries?.length || 0,
          firstPhaseObservations: enrichedData.phase_summaries?.[0]?.observations?.length || 0,
          fullEnrichedStructure: Object.keys(enrichedData)
        });
        
        // Show each observation's swing thoughts
        if (enrichedData.phase_summaries?.[0]?.observations) {
          enrichedData.phase_summaries[0].observations.forEach((obs, idx) => {
            console.log(`[DEBUG] Enriched Observation ${idx}:`, {
              taxonomy_id: obs.taxonomy_id,
              severity: obs.severity,
              confidence: obs.confidence,
              swing_thoughts: obs.swing_thoughts,
              swing_thoughts_length: obs.swing_thoughts?.length || 0,
              drill_ids: obs.drill_ids
            });
          });
        }
      }
      
      console.log('[AnalysisPage] Setting AI results:', { 
        hasEnriched: !!enrichedData, 
        hasRaw: !!updatedRun.ai_mediapipe_v1, 
        analysisData: !!analysisData,
        telemetry: !!updatedRun.telemetry_ai 
      });
      
      // FIX: Normalize analysis data to handle both raw and enriched structures
      const normalizedAnalysis = analysisData ? {
        ...analysisData,
        // Handle enriched data structure (flaws are in phase_summaries[0].observations)
        flaws: Array.isArray(analysisData.flaws) 
          ? analysisData.flaws 
          : (analysisData.phase_summaries?.[0]?.observations || []),
        drills: Array.isArray(analysisData.drills) ? analysisData.drills : [],
        swing_thoughts: Array.isArray(analysisData.swing_thoughts) ? analysisData.swing_thoughts : [],
        // Preserve enriched fields
        coaching_bullets: Array.isArray(analysisData.coaching_bullets) ? analysisData.coaching_bullets : [],
        coach_narrative: analysisData.coach_narrative || ''
      } : null;
      
      // DEBUG: Show normalization results
      console.log('[DEBUG] Normalization Results:', {
        hadAnalysisData: !!analysisData,
        normalizedFlawsLength: normalizedAnalysis?.flaws?.length || 0,
        normalizedDrillsLength: normalizedAnalysis?.drills?.length || 0,
        normalizedSwingThoughtsLength: normalizedAnalysis?.swing_thoughts?.length || 0,
        normalizedStructure: normalizedAnalysis ? Object.keys(normalizedAnalysis) : []
      });
      
      // Analysis results are no longer rendered here - redirect to authoritative page instead

      // Extract frame counts from response
      const framesAckedByAI = data?.framesProcessed || updatedRun.processed_frames || 0;
      const framesSentToAI = data?.framesSentToAI || 0;
      const framesUsedByAI = normalizedAnalysis?.frames_analyzed || framesAckedByAI;

      // Update debug counters
      setDebugCounters(prev => ({
        ...prev,
        framesSentToAI,
        framesAckedByAI,
        framesUsedByAI
      }));

      // FINAL RESULT LOGGING
      console.log('[AI][RESULT]', {
        ok: true,
        counters: { 
          framesSentToAI: debugCounters.framesSentToAI, 
          framesAckedByAI, 
          framesUsedByAI 
        },
        payloadPreview: normalizedAnalysis ? {
          version: normalizedAnalysis.version,
          flaws: normalizedAnalysis.flaws?.length ?? 0,
          drills: normalizedAnalysis.drills?.length ?? 0,
          swing_thoughts: normalizedAnalysis.swing_thoughts?.length ?? 0,
          timestamp: normalizedAnalysis.timestamp
        } : null
      });

      // PIPELINE CHECKPOINT 7: Client Result Display
      console.log('[PIPELINE][7-CLIENT-RESULT]', {
        success: true,
        runId: latestRun.id,
        framesSentToAI: framesSentToAI,
        framesAckedByAI,
        flawsReceived: normalizedAnalysis?.flaws?.length || 0,
        drillsReceived: normalizedAnalysis?.drills?.length || 0,
        totalProcessingTime: Math.round(performance.now() - t0)
      });

      // Persist AI results using AnalysisAPI (shields column drift)
      try {
        if (latestRun?.id) {
          await AnalysisAPI.writeAnalysis({
            runId: latestRun.id,
            aiAnalysis: normalizedAnalysis,
            metadata: {
              model: 'no-doubles-golf-coach',
              analysis_tier: 'professional',
              focus_area: 'full_swing',
              skill_level: 'mid_handicap',
              latency_ms: Date.now() - new Date().getTime(),
              tokens_in: 0,
              tokens_out: 0,
              est_cost_usd: 0,
              runId: latestRun.id,
              created_at: new Date().toISOString()
            },
            useAiMetaColumn: false // Feature flag: use telemetry_ai by default
          });
          
          console.log('[AI][PERSIST] ok', { id: latestRun.id });
        } else {
          console.warn('[AI][PERSIST] No run ID available for persistence');
        }
      } catch (persistError) {
        console.warn('[AI] failed to persist to database:', persistError);
      }

      // Log exact JSON result to console
      console.log('[AI] result payload:', normalizedAnalysis);
      console.log(`[AI] counters { framesSentToAI: ${debugCounters.framesSentToAI}, framesAckedByAI: ${framesAckedByAI}, framesUsedByAI: ${framesUsedByAI} }`);

      // Coach feedback generation removed - analysis results redirect to authoritative page

      // FIX: Show success with detailed status
      const successMessage = data?.cached ? 'AI analysis loaded from cache!' : 'AI analysis completed!';
      const description = enrichedData 
        ? `Analysis enriched with taxonomy data (${data?.stage})` 
        : `Basic analysis completed (${data?.stage})`;
        
      toast.success(successMessage, { description });
      
      // CRITICAL: Redirect to authoritative Analysis page after completion
      // This ensures users always view analysis at /analysis/:analysisId
      if (latestRun?.id) {
        navigate(`/analysis/${latestRun.id}`, { replace: true });
      }

    } catch (error) {
      console.error('AI analysis failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'AI analysis failed';
      setAiAnalysisError(errorMessage);
      toast.error('AI analysis failed', {
        description: errorMessage
      });
    } finally {
      setAiAnalysisLoading(false);
    }
  }, [idsReady, aiAnalysisLoading, videoAssetId, navigate]);

  // Handle start analysis
  const handleStartAnalysis = async () => {
    // FIX: Guard video ref access
    const video = videoRef?.current;
    
    // TRIPWIRE: MediaPipe button diagnostics - removed videoReady requirement
    const frames = telemetryFrames.length;
    const hasFn = !!startAnalysis;
    const ok = !!videoRef.current && hasFn && mpIdsReady;
    
    console.log('[GATE] mediapipe:start', { 
      ok, 
      failedCheck: !videoRef.current ? 'video_ref_missing' : 
                  !hasFn ? 'runner_missing' :
                  !mpIdsReady ? 'ids_not_ready' : null,
      meta: { frames, hasVideo: !!videoRef.current, hasFn, mpIdsReady, mpState } 
    });
    
    if (!ok) {
      const reason = !videoRef.current ? 'Video element not found' : 
                    !hasFn ? 'MediaPipe function not available' :
                    !mpIdsReady ? 'Video IDs not ready' : 'Unknown error';
      toast.error(`Cannot start analysis: ${reason}`);
      return;
    }
    
    if (!video) return;

    console.log('[GATE] mediapipe:run_button', { starting: true, mpState });
    
    try {
      // FIX: Always ensure video is playing when starting analysis
      console.log('[MediaPipe] Initiating video playback for analysis...');
      
      // Load video if not loaded
      if (video.readyState < 1) {
        console.log('[MediaPipe] Loading video metadata...');
        video.load();
        
        // Wait for metadata to load
        await new Promise<void>((resolve, reject) => {
          const timeout = setTimeout(() => {
            video.removeEventListener('loadedmetadata', onLoaded);
            video.removeEventListener('error', onError);
            reject(new Error('Video metadata load timeout'));
          }, 10000);
          
          const onLoaded = () => {
            clearTimeout(timeout);
            video.removeEventListener('loadedmetadata', onLoaded);
            video.removeEventListener('error', onError);
            resolve();
          };
          
          const onError = () => {
            clearTimeout(timeout);
            video.removeEventListener('loadedmetadata', onLoaded);
            video.removeEventListener('error', onError);
            reject(new Error('Failed to load video metadata'));
          };
          
          if (video.readyState >= 1) {
            clearTimeout(timeout);
            resolve();
          } else {
            video.addEventListener('loadedmetadata', onLoaded);
            video.addEventListener('error', onError);
          }
        });
      }
      
       // Start video playback (user-initiated action)
       if (video.paused) {
         console.log('[MediaPipe] Starting video playback...');
         await video.play();
       }
       
       console.log('[TelemetryAnalysis] start', { mpState: 'about to call startAnalysis' });
       await startAnalysis(video);
       console.log('[TelemetryAnalysis] done', { mpState: 'startAnalysis completed' });
      
    } catch (error) {
      console.error('[MediaPipe] startAnalysis failed:', error);
      toast.error(`MediaPipe start failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  // Handle stop analysis

  
  // Helper to resolve and log the latest completed run
  const resolveLatestCompletedRunAndLog = useCallback(async () => {
    const videoUUID = currentVideo?.id;
    if (!videoUUID) {
      console.log('[RUN][RESOLVE][MISS]', { video_id: null });
      return null;
    }
    
    console.log('[RUN][RESOLVE][SEARCH]', { 
      video_id: videoUUID, 
      query: "SELECT * FROM swing_analysis_runs WHERE video_asset_id=? AND status='completed' ORDER BY created_at DESC LIMIT 1" 
    });
    
    try {
      const { data: runs, error } = await supabase
        .from('swing_analysis_runs')
        .select('*')
        .eq('video_asset_id', videoUUID)
        .eq('status', 'completed')
        .order('created_at', { ascending: false })
        .limit(1);
        
      if (error) {
        console.log('[RUN][RESOLVE][ERROR]', { video_id: videoUUID, error: String(error) });
        return null;
      }
      
      const row = runs?.[0];
      if (!row) {
        console.log('[RUN][RESOLVE][MISS]', { video_id: videoUUID });
        return null;
      }
      
      console.log('[RUN][RESOLVE][DONE]', { 
        video_id: videoUUID, 
        runId: row.id, 
        storagePath: row.storage_path, 
        processedFrames: row.processed_frames, 
        hasMetrics: !!row.metrics 
      });
      
      return row;
    } catch (e) {
      console.log('[RUN][RESOLVE][ERROR]', { video_id: videoUUID, error: String(e) });
      return null;
    }
  }, [video]);

  // Verify run resolver for current video (used after Force Save)
  const verifyRunForCurrentVideo = useCallback(async () => {
    const muxAssetId = currentVideo?.muxAssetId;
    if (!muxAssetId) {
      console.log('[RUN][VERIFY][SKIP] No muxAssetId');
      return;
    }
    
    console.log('[RUN][RESOLVE][SEARCH]', {
      searching_for_video_asset_id: muxAssetId,
      query: 'SELECT * FROM swing_analysis_runs WHERE video_asset_id = ? AND status = completed'
    });

    try {
      const { data: runs, error } = await supabase
        .from('swing_analysis_runs')
        .select('*')
        .eq('video_asset_id', muxAssetId)
        .eq('status', 'completed')
        .order('created_at', { ascending: false })
        .limit(1);

      console.log('[RUN][RESOLVE]', {
        video_id: muxAssetId,
        runId: runs?.[0]?.id ?? null,
        status: runs?.[0]?.status ?? null,
        totalFound: runs?.length ?? 0,
        error: error?.message ?? null
      });

      if (!runs || runs.length === 0) {
        console.warn('[RUN][RESOLVE][MISS]', { video_id: muxAssetId });
        return null;
      }

      return runs[0];
    } catch (e) {
      console.log('[RUN][RESOLVE][ERROR]', { video_id: muxAssetId, error: String(e) });
      return null;
    }
  }, [video]);

  const handleStopClick = useCallback(async () => {
    console.log('[UI][STOP][CLICK]');
    const wd = setTimeout(() =>
      console.warn('[WATCHDOG][STOP] stopAnalysis took >3s'), 3000);
    try {
      await stopAnalysis();
    } finally { 
      clearTimeout(wd); 
    }
    console.log('[UI][STOP][DONE]');
  }, [stopAnalysis]);

  const handleForceSaveClick = useCallback(async () => {
    if (!saveEnabled) return;
    
    console.log('[UI][SAVE][CLICK]');
    const wd = setTimeout(() =>
      console.warn('[WATCHDOG][SAVE] persistCompletedRun took >5s'), 5000);
    
    try {
      const res = await persistCompletedRun('manual_click');
      console.log('[UI][SAVE][RESULT]', res);
      
      if (res?.ok) {
        // After successful save, trigger resolver verification
         if (currentVideo?.id) {
          try {
            const { data: runs, error: resolverError } = await supabase
              .from('swing_analysis_runs')
              .select('id, storage_path, processed_frames')
              .eq('video_id', currentVideo.id)
              .eq('status', 'completed')
              .order('created_at', { ascending: false });
            
            console.log('[RUN][RESOLVE][AFTER_SAVE]', { 
              video_id: currentVideo.id,
              totalFound: runs?.length || 0,
              runs: runs?.slice(0, 3) // Show first 3 for debugging
            });
          } catch (resolverErr) {
            console.warn('[RUN][RESOLVE][AFTER_SAVE][ERROR]', resolverErr);
          }
        }
        
        toast.success(`Saved ${res.processedFrames} frames successfully`);
      } else {
        console.log('[UI][SAVE][ERROR]', { error: res?.error || 'unknown' });
        toast.error(`Force save failed: ${res?.error || 'Unknown error'}`);
      }
    } catch (error: any) {
      console.error('[UI][SAVE][EXCEPTION]', error);
      toast.error(`Save failed: ${error.message || 'Unknown error'}`);
    } finally {
      clearTimeout(wd);
    }
  }, [saveEnabled, persistCompletedRun, currentVideo?.id, toast]);

  // FIX: AI Analysis - enabled with Step 9 enrichment
  const canRunAiAnalysis = idsReady && mpState !== 'running';

  // No video ID provided
  if (!videoId) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <div className="max-w-4xl mx-auto px-5 py-8">
          <div className="mb-6">
            <h1 className="text-3xl font-semibold text-slate-900 mb-2">
              Swing Lab Analysis
            </h1>
            <p className="text-slate-600">
              AI-powered analysis and insights for your golf swing videos.
            </p>
          </div>

          <StatusBanner
            type="info"
            title="Development Phase"
            description="This is a scaffold for Swing Lab v1. Mux integration working; ML analysis comes in Step 2."
            className="mb-8"
          />

          {/* Empty State */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-12 text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <BarChart3 className="h-8 w-8 text-white" />
            </div>
            
            <h3 className="text-xl font-semibold text-slate-900 mb-2">
              No Analysis Yet
            </h3>
            <p className="text-slate-600 mb-8 max-w-md mx-auto">
              Upload a swing video to see detailed AI analysis with metrics, coach feedback, and improvement recommendations.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 justify-center mb-8">
              <Link
                to="/swing-lab/upload"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-emerald-500 text-white hover:bg-emerald-600 transition-colors font-medium"
              >
                Upload Video
              </Link>
              <Link
                to="/swing-lab/history"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 transition-colors"
              >
                <History className="h-4 w-4" />
                View History
              </Link>
              <Link
                to="/design/aura-preview"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 transition-colors"
              >
                <Palette className="h-4 w-4" />
                Aura Preview
              </Link>
              <Link
                to="/swing-lab/design-mock"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors"
              >
                <FileVideo className="h-4 w-4" />
                Design Mock
              </Link>
            </div>

            {/* Telemetry Preview */}
            <div className="border-t border-slate-200 pt-6">
              <p className="text-sm text-slate-500 mb-3">Sample telemetry format:</p>
              <TelemetryBadges />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Video with ID provided
  const withinLimits = video ? isWithinSwingLabLimits(video) : true;
  const warningMessage = video ? getLimitWarningMessage(video) : null;

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="max-w-6xl mx-auto px-5 py-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-4">
              <Link 
                to="/swing-lab/upload"
                className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Upload
              </Link>
              <Link 
                to="/swing-lab/history"
                className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 transition-colors"
              >
                <History className="h-4 w-4" />
                View History
              </Link>
              <Button
                variant="outline"
                size="sm"
                onClick={() => openModal({ 
                  currentPage: '/swing-lab/analysis',
                  runId: latestRun?.id 
                })}
                className="inline-flex items-center gap-1 text-sm"
              >
                <MessageSquare className="h-4 w-4" />
                Report an Issue
              </Button>
            </div>
            {video && (
              <ExportMenu 
                analysisData={null}
                telemetryData={null}
                runId={videoId || 'unknown'}
              />
            )}
          </div>
          <h1 className="text-3xl font-semibold text-slate-900 mb-2">
            Swing Lab Analysis
          </h1>
          <p className="text-slate-600">
            Video playback and analysis for your golf swing.
          </p>
        </div>

        {/* Status */}
        <StatusBanner
          type="info"
          title="Development Phase"
          description="Mux playback working. ML analysis and telemetry will be added in Step 2."
          className="mb-8"
        />

        {/* Analysis Status */}
        {mpState === 'loading-models' && (
          <StatusBanner
            type="info"
            title="Loading MediaPipe models..."
            description="Initializing pose and hand detection models."
            className="mb-8"
          />
        )}
        
        {mpState === 'running' && (
          <StatusBanner
            type="info"
            title="Analyzing..."
            description={`Processing frame ${mpStats.frames}${mpStats.duration ? ` • ${mpStats.duration.toFixed(1)}s` : ''}`}
            className="mb-8"
          />
        )}
        
        {mpState === 'persisting' && (
          <StatusBanner
            type="info"
            title="Saving..."
            description="Uploading analysis data to storage."
            className="mb-8"
          />
        )}
        
        {mpState === 'saved' && (
          <StatusBanner
            type="success"
            title="Analysis Complete ✓"
            description={`Saved ${mpStats.frames} frames${mpStats.duration ? ` (${mpStats.duration.toFixed(1)}s)` : ''}`}
            className="mb-8"
          />
        )}
        
        {mpState === 'error' && mpError && (
          <StatusBanner
            type="error"
            title="Analysis Failed"
            description={mpError}
            className="mb-8"
          />
        )}

        {/* Limit Warning */}
        {!withinLimits && warningMessage && (
          <StatusBanner
            type="warning"
            title="Outside Swing Lab Limits"
            description={`${warningMessage}. Swing Lab analysis is tuned for clips ≤15s at 1080p. Higher resolutions/longer clips may increase cost/time.`}
            className="mb-8"
          />
        )}

        {videoError && (
          <AnalysisError 
            error={videoError.message} 
            videoId={videoId || undefined}
            assetId={assetId || undefined}
          />
        )}

        {/* Signed URL Error (for signed videos only) */}
        {video?.playbackPolicy !== 'public' && urlError && (
          <StatusBanner
            type="error"
            title="Signed URL Error"
            description={`Failed to get signed playback URL: ${urlError.message}`}
            className="mb-8"
          />
        )}

        {/* Archived: MuxPlaybackDebug moved to archive/mux */}
        {/* <MuxPlaybackDebug
          video={video}
          signedUrl={signedUrl}
          signedUrlLoading={urlLoading}
          signedUrlError={urlError}
          lastSignerCall={lastSignerCall}
          onRefreshSigner={refetchUrl}
        /> */}

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left: Video Player */}
          <div className="lg:col-span-2">
            {currentVideo?.status === 'processing' ? (
              <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-8 text-center">
                <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  Processing Video
                </h3>
                <p className="text-slate-600 mb-4">
                  Your video is being processed. This usually takes 1-2 minutes.
                </p>
                <p className="text-sm text-slate-500">
                  You can leave this page and come back later. We'll email you when it's ready.
                </p>
              </div>
            ) : video && (video.muxPlaybackId || signedUrl) ? (
              <div className="space-y-6">
                {/* Video Player */}
                <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                  <div className="relative" ref={playerHostRef}>
                    <VideoPlayer
                      signedUrl={video.playbackPolicy === 'public' ? undefined : signedUrl}
                      isLoading={urlLoading}
                      error={urlError}
                      onNativeVideo={handleVideoRef}
                      onPlayStateChange={(playing) => setIsVideoPlaying(playing)}
                      video={video}
                      className="w-full"
                    />
                    <OverlayController
                      videoRef={videoRef}
                      getLatestFrame={getLatestFrame}
                      getFrameAtTime={telemetryReader?.ready ? telemetryReader.getFrameAt : getFrameAtTime}
                      isLiveCapture={isLiveCapture && !telemetryReader?.ready}
                      visible={showOverlay && (telemetryReady || isLiveCapture)}
                      opacity={opacity}
                      showSkeleton={showSkeleton}
                      handsOnly={handsOnly}
                      poseData={currentPoseData}
                    />
                  </div>
                </div>

                {/* Video Controls */}
                <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-slate-900">MediaPipe Analysis</h3>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowDebugPanel(!showDebugPanel)}
                      >
                        <Settings className="h-4 w-4" />
                        Debug
                      </Button>
                    </div>
                  </div>

                  {/* Status Line */}
                  <div className="mb-4 p-3 bg-slate-100 rounded-lg text-sm font-mono">
                    Frames buffered: {framesBuffered} | Has unsaved: {hasUnsavedFrames ? 'yes' : 'no'} | State: {mpState}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    <Button
                      onClick={handleStartAnalysis}
                      disabled={!mpIdsReady || mpState === 'running'}
                      className="bg-emerald-500 hover:bg-emerald-600 text-white"
                    >
                      <Play className="h-4 w-4 mr-2" />
                      {mpState === 'running' ? 'Running...' : 'Start Analysis'}
                    </Button>
                    
                    <Button
                      onClick={handleStopClick}
                      disabled={!stopEnabled}
                      variant="outline"
                    >
                      <Square className="h-4 w-4 mr-2" />
                      Stop
                    </Button>
                    
                    <Button
                      onClick={handleForceSaveClick}
                      disabled={!saveEnabled}
                      variant="outline"
                      className="bg-amber-50 hover:bg-amber-100 border-amber-300"
                    >
                      <Save className="h-4 w-4 mr-2" />
                      Force Save Telemetry ({framesBuffered})
                    </Button>
                    
                    <Button
                      onClick={toggleOverlay}
                      onKeyDown={(e) => {
                        if (e.key === 'Space' || e.key === 'Enter') {
                          e.preventDefault();
                          toggleOverlay();
                        }
                      }}
                      variant="outline"
                      size="sm"
                      className={showOverlay ? "bg-green-50 border-green-200 focus:ring-2 focus:ring-green-500 focus:ring-offset-2" : "focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"}
                      aria-label={`${showOverlay ? 'Hide' : 'Show'} pose overlay on video`}
                      aria-pressed={showOverlay}
                    >
                      {showOverlay ? <EyeOff className="h-4 w-4 mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
                      {showOverlay ? 'Hide Overlay' : 'Show Overlay'}
                    </Button>
                  </div>

                  {/* Overlay Controls */}
                  {showOverlay && (
                    <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="opacity">Opacity</Label>
                        <span className="text-sm text-slate-600">{Math.round(opacity * 100)}%</span>
                      </div>
                      <Slider
                        id="opacity"
                        value={[opacity]}
                        onValueChange={([value]) => setOpacity(value)}
                        max={1}
                        min={0}
                        step={0.1}
                        className="w-full"
                      />
                      
                      <div className="flex items-center space-x-2">
                        <Switch
                          id="hands-only"
                          checked={handsOnly}
                          onCheckedChange={setHandsOnly}
                        />
                        <Label htmlFor="hands-only">Hands only</Label>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Switch
                          id="show-skeleton"
                          checked={showSkeleton}
                          onCheckedChange={setShowSkeleton}
                        />
                        <Label htmlFor="show-skeleton">Show skeleton</Label>
                      </div>
                    </div>
                  )}

                  {/* Analysis Stats */}
                  {mpStats.frames > 0 && (
                    <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                      <h4 className="font-medium text-blue-900 mb-2">Analysis Stats</h4>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-blue-700">Frames:</span>
                          <span className="ml-2 font-mono">{mpStats.frames}</span>
                        </div>
                        <div>
                          <span className="text-blue-700">Duration:</span>
                          <span className="ml-2 font-mono">{formatDuration(mpStats.duration)}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Debug Counters */}
                  {(debugCounters.framesFound > 0 || debugCounters.framesParsed > 0) && (
                    <div className="mt-4 p-3 bg-slate-50 rounded-lg border">
                      <h4 className="text-sm font-medium text-slate-700 mb-2">Debug Counters</h4>
                      <div className="text-xs text-slate-600 font-mono">
                        {debugCounters.framesFound} | {debugCounters.framesParsed} | {debugCounters.framesSentToAI} | {debugCounters.framesAckedByAI} | {debugCounters.framesUsedByAI}
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        framesFound | framesParsed | framesSentToAI | framesAckedByAI | framesUsedByAI
                      </div>
                    </div>
                  )}
                </div>

                {/* AI Analysis Section - Placeholder */}
                <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-slate-900">AI Analysis</h3>
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={handleRunAiAnalysis}
                        disabled={!canRunAiAnalysis || aiAnalysisLoading}
                        className="bg-purple-500 hover:bg-purple-600 text-white"
                      >
                        {aiAnalysisLoading ? 'Analyzing...' : 'Run AI Analysis'}
                      </Button>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            className="inline-flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
                            aria-label="What this analysis does"
                          >
                            <Info className="h-4 w-4" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-xs">
                          <div className="space-y-1">
                            <p className="font-semibold">What this does</p>
                            <p>Analyzes your swing patterns and explains what's affecting your consistency.</p>
                            <p>Uses your uploaded video — no re-upload required.</p>
                            <p>Takes about a moment.</p>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </div>

                  {aiAnalysisError && (
                    <Alert className="mb-4">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{aiAnalysisError}</AlertDescription>
                    </Alert>
                  )}

                  {/* Telemetry Error Banner */}
                  {!telemetryReady && !isLiveCapture && (
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg mb-4">
                      <div className="flex items-start">
                        <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 mr-3 flex-shrink-0" />
                        <div>
                          <h4 className="font-medium text-amber-900 mb-1">Telemetry Unavailable</h4>
                          <p className="text-sm text-amber-700">
                            No saved analysis data found. Re-run analysis to generate telemetry data.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {aiAnalysisLoading && (
                    <div className="mt-4">
                      <ProcessingNarrative />
                    </div>
                  )}
                </div>


                {/* OpenAI Debug Data - Raw Request/Response */}
                {openaiDebugData && (
                  <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4">🔍 OpenAI Debug Data</h3>
                    <div className="space-y-4">
                      
                      {/* Request Data */}
                      <details className="border border-slate-200 rounded-lg">
                        <summary className="cursor-pointer p-3 bg-slate-50 hover:bg-slate-100 rounded-lg font-medium text-slate-700">
                          📤 Raw Request to OpenAI
                          {openaiDebugData.request && (
                            <span className="ml-2 text-xs text-slate-500">
                              ({openaiDebugData.request.messages?.[1]?.content?.length || 0} chars)
                            </span>
                          )}
                        </summary>
                        <div className="p-4 bg-white">
                          {openaiDebugData.request ? (
                            <div className="space-y-2">
                              <div className="text-sm">
                                <span className="font-medium text-slate-700">Model:</span> 
                                <span className="ml-2 font-mono text-slate-900">{openaiDebugData.request.model}</span>
                              </div>
                              <div className="text-sm">
                                <span className="font-medium text-slate-700">Temperature:</span> 
                                <span className="ml-2 font-mono text-slate-900">{openaiDebugData.request.temperature}</span>
                              </div>
                              <div className="text-sm">
                                <span className="font-medium text-slate-700">Max Tokens:</span> 
                                <span className="ml-2 font-mono text-slate-900">{openaiDebugData.request.max_tokens}</span>
                              </div>
                              <div className="text-sm">
                                <span className="font-medium text-slate-700">Contains Landmark Data:</span> 
                                <span className="ml-2 font-mono text-slate-900">
                                  {openaiDebugData.request.messages?.[1]?.content?.includes('"landmarks":') ? '✅ Yes' : '❌ No'}
                                </span>
                              </div>
                              <details className="mt-3">
                                <summary className="cursor-pointer text-sm font-medium text-slate-700 hover:text-slate-900">
                                  View Full Request Payload
                                </summary>
                                <pre className="mt-2 p-3 bg-slate-50 rounded text-xs overflow-auto max-h-96 text-slate-700">
                                  {JSON.stringify(openaiDebugData.request, null, 2)}
                                </pre>
                              </details>
                            </div>
                          ) : (
                            <p className="text-sm text-slate-500">No request data captured</p>
                          )}
                        </div>
                      </details>

                      {/* Response Data */}
                      <details className="border border-slate-200 rounded-lg">
                        <summary className="cursor-pointer p-3 bg-slate-50 hover:bg-slate-100 rounded-lg font-medium text-slate-700">
                          📥 Raw Response from OpenAI
                          {openaiDebugData.response && (
                            <span className="ml-2 text-xs text-slate-500">
                              ({openaiDebugData.response.usage?.total_tokens || 0} tokens)
                            </span>
                          )}
                        </summary>
                        <div className="p-4 bg-white">
                          {openaiDebugData.response ? (
                            <div className="space-y-2">
                              <div className="text-sm">
                                <span className="font-medium text-slate-700">Total Tokens:</span> 
                                <span className="ml-2 font-mono text-slate-900">{openaiDebugData.response.usage?.total_tokens || 0}</span>
                              </div>
                              <div className="text-sm">
                                <span className="font-medium text-slate-700">Prompt Tokens:</span> 
                                <span className="ml-2 font-mono text-slate-900">{openaiDebugData.response.usage?.prompt_tokens || 0}</span>
                              </div>
                              <div className="text-sm">
                                <span className="font-medium text-slate-700">Completion Tokens:</span> 
                                <span className="ml-2 font-mono text-slate-900">{openaiDebugData.response.usage?.completion_tokens || 0}</span>
                              </div>
                              <details className="mt-3">
                                <summary className="cursor-pointer text-sm font-medium text-slate-700 hover:text-slate-900">
                                  View Full Response Data
                                </summary>
                                <pre className="mt-2 p-3 bg-slate-50 rounded text-xs overflow-auto max-h-96 text-slate-700">
                                  {JSON.stringify(openaiDebugData.response, null, 2)}
                                </pre>
                              </details>
                            </div>
                          ) : (
                            <p className="text-sm text-slate-500">No response data captured</p>
                          )}
                        </div>
                      </details>

                      {/* Raw Content */}
                      <details className="border border-slate-200 rounded-lg">
                        <summary className="cursor-pointer p-3 bg-slate-50 hover:bg-slate-100 rounded-lg font-medium text-slate-700">
                          📄 Raw Content from OpenAI
                          {openaiDebugData.content && (
                            <span className="ml-2 text-xs text-slate-500">
                              ({openaiDebugData.content.length} chars)
                            </span>
                          )}
                        </summary>
                        <div className="p-4 bg-white">
                          {openaiDebugData.content ? (
                            <div>
                              <p className="text-sm text-slate-600 mb-3">
                                This is the exact JSON string returned by OpenAI before any processing:
                              </p>
                              <pre className="p-3 bg-slate-50 rounded text-xs overflow-auto max-h-96 text-slate-700">
                                {openaiDebugData.content}
                              </pre>
                            </div>
                          ) : (
                            <p className="text-sm text-slate-500">No content data captured</p>
                          )}
                        </div>
                      </details>

                      <div className="text-xs text-slate-500 mt-4">
                        Debug data captured at: {openaiDebugData.timestamp}
                      </div>
                    </div>
                  </div>
                )}

                {/* OpenAI Debug Data - Raw Request/Response */}
                {openaiDebugData && (
                  <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4">🔍 OpenAI Debug Data</h3>
                    <div className="space-y-4">
                      
                      {/* Request Data */}
                      <details className="border border-slate-200 rounded-lg">
                        <summary className="cursor-pointer p-3 bg-slate-50 hover:bg-slate-100 rounded-lg font-medium text-slate-700">
                          📤 Raw Request to OpenAI
                          {openaiDebugData.request && (
                            <span className="ml-2 text-xs text-slate-500">
                              ({openaiDebugData.request.messages?.[1]?.content?.length || 0} chars)
                            </span>
                          )}
                        </summary>
                        <div className="p-4 bg-white">
                          {openaiDebugData.request ? (
                            <div className="space-y-2">
                              <div className="text-sm">
                                <span className="font-medium text-slate-700">Model:</span> 
                                <span className="ml-2 font-mono text-slate-900">{openaiDebugData.request.model}</span>
                              </div>
                              <div className="text-sm">
                                <span className="font-medium text-slate-700">Temperature:</span> 
                                <span className="ml-2 font-mono text-slate-900">{openaiDebugData.request.temperature}</span>
                              </div>
                              <div className="text-sm">
                                <span className="font-medium text-slate-700">Max Tokens:</span> 
                                <span className="ml-2 font-mono text-slate-900">{openaiDebugData.request.max_tokens}</span>
                              </div>
                              <div className="text-sm">
                                <span className="font-medium text-slate-700">Contains Landmark Data:</span> 
                                <span className="ml-2 font-mono text-slate-900">
                                  {openaiDebugData.request.messages?.[1]?.content?.includes('"landmarks":') ? '✅ Yes' : '❌ No'}
                                </span>
                              </div>
                              <details className="mt-3">
                                <summary className="cursor-pointer text-sm font-medium text-slate-700 hover:text-slate-900">
                                  View Full Request Payload
                                </summary>
                                <pre className="mt-2 p-3 bg-slate-50 rounded text-xs overflow-auto max-h-96 text-slate-700">
                                  {JSON.stringify(openaiDebugData.request, null, 2)}
                                </pre>
                              </details>
                            </div>
                          ) : (
                            <p className="text-sm text-slate-500">No request data captured</p>
                          )}
                        </div>
                      </details>

                      {/* Response Data */}
                      <details className="border border-slate-200 rounded-lg">
                        <summary className="cursor-pointer p-3 bg-slate-50 hover:bg-slate-100 rounded-lg font-medium text-slate-700">
                          📥 Raw Response from OpenAI
                          {openaiDebugData.response && (
                            <span className="ml-2 text-xs text-slate-500">
                              ({openaiDebugData.response.usage?.total_tokens || 0} tokens)
                            </span>
                          )}
                        </summary>
                        <div className="p-4 bg-white">
                          {openaiDebugData.response ? (
                            <div className="space-y-2">
                              <div className="text-sm">
                                <span className="font-medium text-slate-700">Total Tokens:</span> 
                                <span className="ml-2 font-mono text-slate-900">{openaiDebugData.response.usage?.total_tokens || 0}</span>
                              </div>
                              <div className="text-sm">
                                <span className="font-medium text-slate-700">Prompt Tokens:</span> 
                                <span className="ml-2 font-mono text-slate-900">{openaiDebugData.response.usage?.prompt_tokens || 0}</span>
                              </div>
                              <div className="text-sm">
                                <span className="font-medium text-slate-700">Completion Tokens:</span> 
                                <span className="ml-2 font-mono text-slate-900">{openaiDebugData.response.usage?.completion_tokens || 0}</span>
                              </div>
                              <details className="mt-3">
                                <summary className="cursor-pointer text-sm font-medium text-slate-700 hover:text-slate-900">
                                  View Full Response Data
                                </summary>
                                <pre className="mt-2 p-3 bg-slate-50 rounded text-xs overflow-auto max-h-96 text-slate-700">
                                  {JSON.stringify(openaiDebugData.response, null, 2)}
                                </pre>
                              </details>
                            </div>
                          ) : (
                            <p className="text-sm text-slate-500">No response data captured</p>
                          )}
                        </div>
                      </details>

                      {/* Raw Content */}
                      <details className="border border-slate-200 rounded-lg">
                        <summary className="cursor-pointer p-3 bg-slate-50 hover:bg-slate-100 rounded-lg font-medium text-slate-700">
                          📄 Raw Content from OpenAI
                          {openaiDebugData.content && (
                            <span className="ml-2 text-xs text-slate-500">
                              ({openaiDebugData.content.length} chars)
                            </span>
                          )}
                        </summary>
                        <div className="p-4 bg-white">
                          {openaiDebugData.content ? (
                            <div>
                              <p className="text-sm text-slate-600 mb-3">
                                This is the exact JSON string returned by OpenAI before any processing:
                              </p>
                              <pre className="p-3 bg-slate-50 rounded text-xs overflow-auto max-h-96 text-slate-700">
                                {openaiDebugData.content}
                              </pre>
                            </div>
                          ) : (
                            <p className="text-sm text-slate-500">No content data captured</p>
                          )}
                        </div>
                      </details>

                      <div className="text-xs text-slate-500 mt-4">
                        Debug data captured at: {openaiDebugData.timestamp}
                      </div>
                    </div>
                  </div>
                )}

                {/* Frame Counts Display */}
                <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4">
                  <h4 className="text-sm font-medium text-slate-700 mb-3">Telemetry Status</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-slate-500">Frames Buffered:</span>
                      <span className="ml-2 font-mono text-slate-900">{framesBuffered || 0}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Telemetry Ready:</span>
                      <span className="ml-2">{telemetryReady ? '✅' : '⏳'}</span>
                    </div>
                    {telemetryFrames?.length > 0 && (
                      <div>
                        <span className="text-slate-500">Replay Frames:</span>
                        <span className="ml-2 font-mono text-slate-900">{telemetryFrames.length}</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Debug Counters (debug mode only) */}
                  {isDebugMode && (debugCounters.framesFound > 0 || debugCounters.framesParsed > 0 || debugCounters.framesSentToAI > 0) && (
                    <details className="mt-4">
                      <summary className="text-xs text-slate-500 cursor-pointer">🔍 Debug Counters</summary>
                      <div className="mt-2 text-xs text-slate-600 font-mono bg-slate-50 p-2 rounded">
                        {debugCounters.framesFound} | {debugCounters.framesParsed} | {debugCounters.framesSentToAI} | {debugCounters.framesAckedByAI} | {debugCounters.framesUsedByAI}
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        framesFound | framesParsed | framesSentToAI | framesAckedByAI | framesUsedByAI
                      </div>
                    </details>
                  )}
                </div>

                {/* Debug Panel */}
                {showDebugPanel && (
                  <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4">Debug Information</h3>
                    <TelemetryPanel 
                      videoId={video.id}
                      playbackId={playbackId}
                      assetId={videoAssetId}
                      mpState={mpState}
                      mpStats={mpStats}
                      mpError={mpError}
                      idsReady={idsReady}
                    />
                  </div>
                )}
              </div>
            ) : videoLoading ? (
              <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-8 text-center">
                <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-slate-600">Loading video...</p>
              </div>
            ) : (
              <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-8 text-center">
                <AlertCircle className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Video Not Found</h3>
                <p className="text-slate-600">
                  The video you're looking for doesn't exist or you don't have permission to view it.
                </p>
              </div>
            )}
          </div>

          {/* Right: Sidebar */}
          <div className="space-y-6">
            {/* Video Info */}
            {video && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Video Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <span className="text-sm font-medium text-slate-700">Title:</span>
                    <p className="text-sm text-slate-900">{video.title || 'Untitled'}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-slate-700">Duration:</span>
                    <p className="text-sm text-slate-900">{formatDuration(video.duration)}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-slate-700">Status:</span>
                    <Badge variant={video.status === 'ready' ? 'default' : 'secondary'}>
                      {video.status}
                    </Badge>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-slate-700">Created:</span>
                    <p className="text-sm text-slate-900">
                      {formatDistanceToNow(new Date(video.createdAt))} ago
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Recent Runs */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Recent Analysis Runs</CardTitle>
              </CardHeader>
              <CardContent>
                <RecentRunsList 
                  videoAssetId={videoAssetId}
                  onSelectRun={handleReloadRun}
                />
              </CardContent>
            </Card>

            {/* Saved Videos */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Other Videos</CardTitle>
              </CardHeader>
              <CardContent>
                <SavedVideosList 
                  currentVideoId={currentVideo?.id}
                  limit={5}
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      
      <ReportIssueModal
        isOpen={isOpen}
        onClose={closeModal}
        runId={context.runId || latestRun?.id}
        currentPage={context.currentPage}
        previousPage={context.previousPage}
      />
    </div>
  );
}